import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router-dom";
import { useApi } from "@/lib/useApi";

const TOKEN_KEY = "gw_token";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));
  const setToken = useCallback((t) => {
    if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY);
    qc.clear();
    setTokenState(t);
  }, [qc]);
  const logout = useCallback(() => setToken(null), [setToken]);
  const value = useMemo(() => ({ token, setToken, logout }), [token, setToken, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuthToken = () => useContext(AuthContext);

export function useMe() {
  const api = useApi();
  const { token } = useAuthToken();
  return useQuery({
    queryKey: ["me", token],
    queryFn: async () => (await api.get("/me")).data,
    enabled: !!token,
    retry: 1,
  });
}

export function RequireAuth({ children }) {
  const loc = useLocation();
  const { token } = useAuthToken();
  if (!token) return <Navigate to="/sign-in" state={{ from: loc }} replace />;
  return children;
}
